import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, ShieldCheck, Sparkles, Clock, Plane, Building2, Wine, CalendarDays } from "lucide-react";
import ModeToggle from "../components/ModeToggle";
import FAQSection from "../components/FAQSection";
import VehicleCard from "../components/VehicleCard";
import HeroImage from "../components/HeroImage";
import HeroSearchBar from "../components/HeroSearchBar";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";

const stagger = {
  animate: { transition: { staggerChildren: 0.08 } },
};
const item = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Home() {
  const [mode, setMode] = useState("chauffeur");
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [specials, setSpecials] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vehicles", { params: { available_for: mode } })
      .then((r) => setFeatured(r.data.slice(0, 6)))
      .catch(() => setFeatured([]));
  }, [mode]);

  useEffect(() => {
    api.get("/events", { params: { featured: true } })
      .then((r) => setEvents(r.data.slice(0, 3)))
      .catch(() => setEvents([]));
    api.get("/specials")
      .then((r) => setSpecials(r.data || []))
      .catch(() => setSpecials([]));
  }, []);

  return (
    <>
      {/* HERO — official Plenty Rydez backdrop (title + branding baked into the image) */}
      <section
        data-testid={TID.home.hero}
        className="relative overflow-hidden noise-overlay -mt-24 pt-24 min-h-[70vh] sm:min-h-[92vh] flex flex-col"
      >
        <HeroImage />
        <div className="relative z-10 flex-1 flex items-end">
          <div className="mx-auto max-w-7xl w-full px-5 sm:px-8 pb-16">
            <motion.div variants={stagger} initial="initial" animate="animate" className="flex flex-col items-center gap-6">
              <motion.div variants={item}>
                <ModeToggle mode={mode} onChange={setMode} />
              </motion.div>
              <motion.div variants={item} className="w-full flex justify-center">
                <HeroSearchBar />
              </motion.div>
              <motion.div variants={item} className="flex flex-wrap gap-3 justify-center">
                <button
                  data-testid={TID.home.ctaBook}
                  onClick={() => navigate(`/fleet?mode=${mode}`)}
                  className="btn-cream px-6 py-3 rounded-full text-sm inline-flex items-center gap-2"
                >
                  {mode === "chauffeur" ? "Book a Ride" : "Reserve a Vehicle"}
                  <ArrowUpRight size={16} />
                </button>
                <Link
                  data-testid={TID.home.ctaBrowse}
                  to="/fleet"
                  className="px-6 py-3 rounded-full text-sm text-white/90 hover:text-white border border-white/20 hover:border-white/40 bg-black/40 backdrop-blur transition-colors inline-flex items-center gap-2"
                >
                  Browse the entire fleet
                </Link>
              </motion.div>
              <motion.div variants={item} className="flex flex-wrap gap-6 sm:gap-8 text-xs sm:text-sm text-white/70 justify-center">
                <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-cream" /> Licensed & insured</div>
                <div className="flex items-center gap-2"><Clock size={14} className="text-cream" /> 24/7 concierge</div>
                <div className="flex items-center gap-2"><MapPin size={14} className="text-cream" /> NY · NJ · PA · DMV · CT · JFK · LGA · EWR</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: Plane, title: "Airport transfers", body: "JFK, LGA, EWR — meet & greet, luggage, quiet." },
            { icon: Building2, title: "Executive roadshows", body: "Multi-stop, board-ready, discreet." },
            { icon: Wine, title: "Weddings & events", body: "Fleet coordination, on-time, on-brand." },
            { icon: Sparkles, title: "VIP nights out", body: "Late-night, luxurious, drama-free." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="glass rounded-2xl p-6 hover:border-gold/30 transition-colors">
              <Icon size={22} className="text-gold mb-3" />
              <div className="font-serif text-xl mb-1.5">{title}</div>
              <p className="text-sm text-white/60 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SPECIALS — dynamic flyer promos managed from Back Office */}
      {specials.length > 0 && (
        <section
          data-testid="home-specials"
          className="mx-auto max-w-7xl px-5 sm:px-8 py-10 sm:py-16"
        >
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="label-caps text-cyan mb-2 inline-flex items-center gap-1.5">
                <Sparkles size={12} /> Limited-time specials
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl">Tap a flyer, lock the rate.</h2>
              <p className="text-white/60 mt-2 text-sm">Promo pricing is applied automatically at checkout.</p>
            </div>
          </div>
          <div className={`grid gap-6 ${specials.length === 1 ? "grid-cols-1 max-w-3xl mx-auto" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
            {specials.map((s) => (
              <Link
                key={s.id}
                to={`/promo/${s.promo_code}`}
                data-testid={`home-special-${s.id}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 hover:border-cyan/60 transition-all shadow-lg hover:shadow-[0_20px_80px_-20px_rgba(57,214,255,0.5)] bg-ink-800/70"
              >
                <div className="aspect-square bg-ink-700 relative overflow-hidden">
                  {s.flyer_url ? (
                    <img src={s.flyer_url} alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">
                      <Sparkles size={64} />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-ink-950 via-ink-950/60 to-transparent">
                    <div className="text-cyan text-[10px] label-caps mb-1">Special</div>
                    <div className="font-serif text-xl text-white leading-tight line-clamp-2">{s.title}</div>
                    <div className="text-xs text-white/70 mt-1">
                      ${s.hourly_rate}/hr · {s.min_hours}-hr min
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED FLEET */}
      <section
        data-testid={TID.home.featuredFleet}
        className="mx-auto max-w-7xl px-5 sm:px-8 py-16"
      >
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="label-caps text-gold mb-2">Featured fleet</div>
            <h2 className="font-serif text-3xl sm:text-4xl">
              {mode === "chauffeur" ? "For being driven" : "For driving yourself"}
            </h2>
          </div>
          <Link to={`/fleet?mode=${mode}`} className="btn-outline-electric px-4 py-2 rounded-full text-xs inline-flex items-center gap-1 hover:text-white">
            View all <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((v) => (
            <VehicleCard key={v.id} vehicle={v} defaultMode={mode} />
          ))}
        </div>
      </section>

      {/* FEATURED EVENTS & PROMOTIONS */}
      {events.length > 0 && (
        <section
          data-testid="home-events"
          className="mx-auto max-w-7xl px-4 sm:px-8 py-16 sm:py-20"
        >
          <div className="flex items-end justify-between mb-10 sm:mb-12 flex-wrap gap-4">
            <div>
              <div className="label-caps text-gold mb-2 inline-flex items-center gap-1.5">
                <CalendarDays size={12} /> Featured Events &amp; Promotions
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">Roll in style. Skip the parking.</h2>
              <p className="text-white/60 mt-3 max-w-xl text-sm sm:text-base">Tap any flyer to book a chauffeured ride for these events.</p>
            </div>
          </div>
          <div className={`grid gap-6 sm:gap-8 ${events.length === 1 ? "grid-cols-1 max-w-2xl mx-auto" : events.length === 2 ? "grid-cols-1 sm:grid-cols-2 max-w-5xl mx-auto" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
            {events.map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                data-testid={`home-event-${e.id}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 hover:border-gold/60 transition-all bg-ink-800/70 shadow-lg hover:shadow-[0_10px_60px_-10px_rgba(212,175,55,0.35)]"
              >
                <div className="aspect-[3/4] bg-ink-700 relative overflow-hidden">
                  {e.flyer_url ? (
                    <img src={e.flyer_url} alt={e.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">
                      <CalendarDays size={64} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/50 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="label-caps text-[10px] bg-gold text-ink-900 rounded-full px-3 py-1 font-bold">
                      {e.category || "event"}
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                    <div className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3 line-clamp-2">{e.title}</div>
                    <div className="text-sm text-white/80 flex items-center gap-2 mb-1">
                      <CalendarDays size={14} className="text-gold flex-shrink-0" />
                      {new Date(e.event_date).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                    {e.location && (
                      <div className="text-sm text-white/70 flex items-center gap-2">
                        <MapPin size={14} className="text-gold flex-shrink-0" /> <span className="line-clamp-1">{e.location}</span>
                      </div>
                    )}
                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-cream label-caps bg-cream/10 border border-cream/30 rounded-full px-4 py-2 group-hover:bg-cream group-hover:text-ink-900 transition-all">
                      Book a ride <ArrowUpRight size={14} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* RENTAL POLICY BAND */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="glass rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="label-caps text-electric mb-3">Rental policy</div>
            <h3 className="font-serif text-3xl sm:text-4xl mb-4">Two-day minimum. Valid license, always.</h3>
            <p className="text-white/70 leading-relaxed">
              All self-drive rentals are booked for a <span className="text-gold font-semibold">two-day minimum</span>.
              A valid, in-date driver’s license is required at pickup. The Kia K4 and Kia Sportage are
              <span className="text-gold"> approved for Uber, Lyft, Uber Eats and DoorDash</span> use.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              "2-day minimum",
              "Rideshare & delivery approved (K4 · Sportage)",
              "Valid driver's license required",
              "50% refundable deposit",
              "Insurance included",
            ].map((tag) => (
              <span key={tag} className="px-4 py-2 rounded-full bg-ink-800/80 border border-white/10 text-sm">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED */}
      <FAQSection limit={6} />
    </>
  );
}
